<template>
  <div>
    <!-- Overview (matches ProductOverview.vue) -->
    <b-row class="mb-0">
      <b-col cols="12">
        <b-card class="mb-3">
          <div class="d-flex justify-content-between align-items-center mb-1">
            <h6 class="mb-0">
              <b>Report Overview: {{ d.sidebar.topbox.Report }}</b>
              <span class="ml-1">|</span>
              <span class="ml-1">Product Status: {{ d.status }}</span>
            </h6>
            <div class="d-flex">
              <b-button
                variant="outline-secondary"
                size="sm"
                class="mr-1"
                disabled
              >
                <feather-icon
                  icon="FileTextIcon"
                  size="14"
                  class="mr-50"
                />
                Client PDF
              </b-button>
              <b-button
                variant="outline-secondary"
                size="sm"
                disabled
              >
                <feather-icon
                  icon="FileIcon"
                  size="14"
                  class="mr-50"
                />
                Adviser PDF
              </b-button>
            </div>
          </div>
          <h1
            id="report-title"
            class="d-flex align-items-center"
          >
            <b-avatar
              class="mr-1"
              :variant="d.pticoncolor"
              size="48"
              rounded="lg"
            >
              <feather-icon
                size="24"
                :icon="d.pticonname"
              />
            </b-avatar>
            {{ d.reportTitle }}
          </h1>
          <b-row class="match-height mt-2 mb-2">
            <b-col
              cols="12"
              md="3"
            >
              <div class="d-flex justify-content-center align-items-start">
                <img
                  class="mb-0 shadow"
                  width="100"
                  :src="`${baseURL}servethumbnail.cfm?id=${d.INTERNALINFO.FVCDETAILSID}`"
                >
              </div>
            </b-col>
            <b-col
              cols="12"
              md="9"
            >
              <div class="ml-1 mr-1">
                <b-row>
                  <b-col cols="8">
                    <b-row>
                      <b-col
                        cols="6"
                        class="mb-2"
                      >
                        <h4 class="mb-0 text-secondary font-weight-bold">
                          Provider
                        </h4>
                        <h3 class="mb-0">
                          {{ d.sidebar.providerContact.providerName }}
                        </h3>
                      </b-col>
                      <b-col
                        cols="6"
                        class="mb-2"
                      >
                        <h4 class="mb-0 text-secondary font-weight-bold">
                          Counterparty
                        </h4>
                        <h3 class="mb-0">
                          {{ d.FULLCOL.Counterparty.NAME }}
                        </h3>
                      </b-col>
                      <b-col cols="6">
                        <h4 class="mb-0 text-secondary font-weight-bold">
                          Investment Length
                        </h4>
                        <h3 class="mb-0">
                          {{ d.LHSCOL['Investment Length'] }}
                        </h3>
                      </b-col>
                      <b-col cols="6">
                        <h4 class="mb-0 text-secondary font-weight-bold">
                          ISIN
                        </h4>
                        <h3 class="mb-0">
                          {{ d.FULLCOL['Identifier Reference'] || '-' }}
                        </h3>
                      </b-col>
                    </b-row>
                  </b-col>
                  <b-col cols="4">
                    <h4 class="mb-0 text-secondary">
                      Underlying Asset
                    </h4>
                    <h5 class="mb-0">
                      {{ d.FULLCOL['Underlying Asset'][0].NAME }}
                    </h5>
                  </b-col>
                </b-row>
              </div>
            </b-col>
          </b-row>
        </b-card>
      </b-col>
    </b-row>

    <!-- Navigation bar (matches ProductBreakdownNav.vue) -->
    <b-row id="static-nav-bar">
      <b-col cols="12">
        <div class="d-flex">
          <b-button
            variant="primary"
            size="sm"
            class="mr-1 flex-fill py-1 d-flex align-items-center justify-content-center"
          >
            <feather-icon
              icon="BookOpenIcon"
              size="16"
              class="mr-50"
            />
            <span style="transform: translateY(1px)">Overview</span>
          </b-button>
          <b-button
            variant="outline-secondary"
            size="sm"
            class="mr-1 flex-fill py-1 d-flex align-items-center justify-content-center"
          >
            <feather-icon
              icon="InfoIcon"
              size="16"
              class="mr-50"
            />
            <span style="transform: translateY(1px)">Key Information</span>
          </b-button>
          <b-button
            variant="outline-secondary"
            size="sm"
            class="mr-1 flex-fill py-1 d-flex align-items-center justify-content-center"
          >
            <feather-icon
              icon="FileTextIcon"
              size="16"
              class="mr-50"
            />
            <span style="transform: translateY(1px)">Documentation</span>
          </b-button>
          <b-button
            variant="outline-secondary"
            size="sm"
            class="mr-1 flex-fill py-1 d-flex align-items-center justify-content-center"
          >
            <feather-icon
              icon="BarChart2Icon"
              size="16"
              class="mr-50"
            />
            <span style="transform: translateY(1px)">Analysis</span>
          </b-button>
          <b-button
            variant="outline-secondary"
            size="sm"
            class="mr-1 flex-fill py-1 d-flex align-items-center justify-content-center"
          >
            <feather-icon
              icon="CheckCircleIcon"
              size="16"
              class="mr-50"
            />
            <span style="transform: translateY(1px)">Compliance</span>
          </b-button>
          <b-button
            variant="outline-secondary"
            size="sm"
            class="mr-1 flex-fill py-1 d-flex align-items-center justify-content-center"
          >
            <feather-icon
              icon="ActivityIcon"
              size="16"
              class="mr-50"
            />
            <span style="transform: translateY(1px)">Lifecycle</span>
          </b-button>
          <b-button
            variant="outline-secondary"
            size="sm"
            class="flex-fill py-1 d-flex align-items-center justify-content-center"
          >
            <feather-icon
              icon="BriefcaseIcon"
              size="16"
              class="mr-50"
            />
            <span style="transform: translateY(1px)">Provider</span>
          </b-button>
        </div>
      </b-col>
    </b-row>

    <!-- Key Information (matches ProductKeyInformation.vue) -->
    <b-row id="key-information">
      <b-col cols="12">
        <h1 class="mb-2 mt-4">
          Key Information
        </h1>
        <b-card class="mt-1">
          <div class="d-flex justify-content-end align-items-center mb-2">
            <span class="text-secondary">Last updated as of: {{ d.sidebar.topbox['Report created'] }}</span>
          </div>
          <b-row class="mb-1 ml-0 mr-0">
            <b-col
              cols="12"
              md="3"
            >
              <div
                v-for="(value, key) in d.sidebar.topbox"
                :key="key"
                class="mb-2"
              >
                <h3 class="mb-0 text-secondary font-weight-bold">
                  {{ key }}
                </h3>
                <h4 class="mb-3">
                  {{ value }}
                </h4>
              </div>
              <h3 class="mb-0 text-secondary font-weight-bold mt-3">
                Product Summary
              </h3>
              <ul style="padding-left: 0px; list-style-type: none;">
                <li
                  v-for="(item, i) in d.sidebar.productSummary"
                  :key="i"
                  class="mb-0"
                  style="position: relative; padding-left: 15px; font-size: 1rem;"
                >
                  <span style="position: absolute; left: 0;">▸</span>
                  {{ item }}
                </li>
              </ul>
            </b-col>
            <b-col
              cols="12"
              md="9"
            >
              <b-row>
                <b-col
                  cols="12"
                  md="6"
                >
                  <b-table-simple>
                    <tbody>
                      <tr
                        v-for="(value, key) in d.LHSCOL"
                        :key="key"
                      >
                        <td><b>{{ key }}</b></td>
                        <td>{{ value }}</td>
                      </tr>
                    </tbody>
                  </b-table-simple>
                </b-col>
                <b-col
                  cols="12"
                  md="6"
                >
                  <b-table-simple>
                    <tbody>
                      <tr
                        v-for="(value, key) in d.RHSCOL"
                        :key="key"
                      >
                        <td><b>{{ key }}</b></td>
                        <td>{{ value }}</td>
                      </tr>
                    </tbody>
                  </b-table-simple>
                </b-col>
              </b-row>
              <b-row>
                <b-col
                  cols="12"
                  md="8"
                >
                  <b-table-simple>
                    <tbody>
                      <tr>
                        <td><b>Underlying Asset</b></td>
                        <td>{{ d.FULLCOL['Underlying Asset'][0].NAME }}</td>
                      </tr>
                      <tr>
                        <td><b>Counterparty</b></td>
                        <td>{{ d.FULLCOL.Counterparty.NAME }}</td>
                      </tr>
                      <tr>
                        <td><b>Issuer credit rating</b></td>
                        <td>{{ d.FULLCOL['Issuer credit rating'] }}</td>
                      </tr>
                      <tr>
                        <td><b>Tax wrapper(s)</b></td>
                        <td>{{ d.FULLCOL['Tax wrapper(s)'] }}</td>
                      </tr>
                      <tr>
                        <td><b>Min/Max Investment</b></td>
                        <td>{{ d.FULLCOL['Min/Max Investment'] }}</td>
                      </tr>
                      <tr>
                        <td><b>PRIIPs SRI</b></td>
                        <td>{{ d.FULLCOL['PRIIPs SRI'] }}</td>
                      </tr>
                    </tbody>
                  </b-table-simple>
                </b-col>
              </b-row>
            </b-col>
          </b-row>
        </b-card>
      </b-col>
    </b-row>

    <!-- Documentation (matches ProductDocumentation.vue) -->
    <b-row id="documentation">
      <b-col cols="12">
        <h1 class="mb-2 mt-2">
          Documentation
        </h1>
        <div class="pdf-grid mb-2">
          <b-button
            v-for="(_, key) in d.productDocuments"
            :key="key"
            variant="outline-dark"
            class="pdf-button"
            disabled
          >
              <b-img
                height="40"
                :src="require('@/assets/images/icons/file-icons/adobe_pdf_icon_modern.png')"
                class="pdf-icon"
                style="width: auto;"
              />
            <div class="pdf-name">
              {{ key }}
            </div>
          </b-button>
        </div>
      </b-col>
    </b-row>

    <!-- Analysis (matches ProductAnalysis.vue structure) -->
    <b-row id="analysis">
      <b-col cols="12">
        <h1 class="mb-2 mt-2">
          Analysis
        </h1>
        <div class="mb-0">
          <h2 class="mb-2 mt-1">
            Initial Assessment
          </h2>
          <b-row class="match-height mb-0">
            <b-col
              cols="12"
              md="6"
            >
              <b-card>
                <h4>Figure 1: Statistics</h4>
                <p>
                  This figure compares key product statistics to market averages.
                  Overall, price, and return scores (0-10 scale, 10 = most favorable)
                  reflect value and risk-adjusted returns. The riskmap (0-10 scale)
                  shows market and credit risk contributions, plus maturity/duration.
                </p>
                <b-table-simple>
                  <thead>
                    <tr>
                      <th />
                      <th
                        v-for="(heading, i) in d.Assessment.FigureOne.headings.slice(1)"
                        :key="i"
                      >
                        {{ heading }}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="(name, rowIndex) in d.Assessment.FigureOne.names"
                      :key="rowIndex"
                    >
                      <td><strong>{{ name }}</strong></td>
                      <td
                        v-for="(val, colIndex) in d.Assessment.FigureOne.values[rowIndex]"
                        :key="colIndex"
                      >
                        {{ val }}
                      </td>
                    </tr>
                  </tbody>
                </b-table-simple>
              </b-card>
            </b-col>
            <b-col
              cols="12"
              md="6"
            >
              <b-card>
                <h4>Figure 2: Comparison chart</h4>
                <p class="mb-3">
                  This chart plots the riskmap and overall score for all products
                  on the report centre which are open for investment.
                  <br><br>
                  <i><b>Note:</b> The current product is shown with a diamond marker.</i>
                </p>
                <comparison-chart :figure-three="d.Assessment.FigureThree" />
              </b-card>
            </b-col>
          </b-row>
          <b-row>
            <b-col cols="12">
              <b-card>
                <h4>Figure 3: Investor results summary</h4>
                <p>
                  {{ d.Outcomes.FigureFour.intro }}
                  <br>This table shows a number of mutually exclusive outcomes of product performance
                  classifications and associated statistics.
                  <br><br><i>Note: All figures below are rounded.</i>
                </p>
                <b-table-simple>
                  <thead>
                    <tr>
                      <th
                        v-for="(heading, i) in d.Outcomes.FigureFour.headings"
                        :key="i"
                      >
                        {{ heading }}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="(name, rowIndex) in d.Outcomes.FigureFour.names"
                      :key="rowIndex"
                    >
                      <td><strong>{{ name }}</strong></td>
                      <td
                        v-for="(col, colIndex) in d.Outcomes.FigureFour.values"
                        :key="colIndex"
                      >
                        {{ col[rowIndex] }}
                      </td>
                    </tr>
                  </tbody>
                </b-table-simple>
              </b-card>
            </b-col>
          </b-row>
        </div>

        <h2 class="mb-2 mt-2">
          Simulated Product Outcomes
        </h2>
        <div class="mb-0">
          <b-row class="mb-0">
            <b-col cols="12">
              <b-card>
                <h4>Figure 4: Capital</h4>
                <p>{{ d.Outcomes.FigureFive.intro }}</p>
                <b-table-simple>
                  <thead>
                    <tr>
                      <th
                        v-for="(heading, i) in d.Outcomes.FigureFive.headings"
                        :key="i"
                      >
                        {{ heading }}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="(name, rowIndex) in d.Outcomes.FigureFive.names"
                      :key="rowIndex"
                    >
                      <td><strong>{{ name }}</strong></td>
                      <td
                        v-for="(col, colIndex) in d.Outcomes.FigureFive.values"
                        :key="colIndex"
                      >
                        {{ col[rowIndex] }}
                      </td>
                    </tr>
                  </tbody>
                </b-table-simple>
              </b-card>
            </b-col>
          </b-row>
          <b-row class="match-height">
            <b-col
              cols="12"
              md="6"
            >
              <b-card>
                <h4>Figure 5: Payoff chart</h4>
                <p>
                  This chart shows a sample of simulated outcomes of the final underlying value
                  and the product payoff to help identify the risk and return prospects.
                </p>
                <payoff-chart :figure-two="d.Assessment.FigureTwo" />
              </b-card>
            </b-col>
            <b-col
              cols="12"
              md="6"
            >
              <b-card>
                <h4>Figure 6: Charts of product outcomes</h4>
                <p>
                  Charts of distribution of structured product returns based on
                  forward looking simulation (market risk only).
                </p>
                <outcomes-charts :figure-six="d.Outcomes.FigureSix" />
              </b-card>
            </b-col>
          </b-row>
        </div>
      </b-col>
    </b-row>

    <!-- Compliance -->
    <b-row id="compliance">
      <b-col cols="12">
        <h1 class="mb-2 mt-2">
          Compliance
        </h1>
        <div class="mb-0">
          <h2 class="mb-2 mt-1">
            Target Market
          </h2>
          <b-row class="match-height mb-0">
            <b-col
              v-for="section in targetMarket"
              :key="section.sectionname"
              cols="12"
              md="4"
            >
              <b-card>
                <template #header>
                  <div class="d-flex align-items-center">
                    <b-avatar
                      size="38"
                      :variant="section.iconColor"
                      rounded
                      class="mr-1"
                    >
                      <feather-icon
                        size="20"
                        :icon="section.icon"
                      />
                    </b-avatar>
                    <b-card-title class="mb-0">
                      {{ section.sectionname }}
                    </b-card-title>
                  </div>
                </template>
                <b-card-body class="pt-0">
                  <ul
                    class="mb-0"
                    style="padding-left: 0px; list-style-type: none;"
                  >
                    <li
                      v-for="(item, i) in section.data"
                      :key="i"
                      style="position: relative; padding-left: 15px;"
                    >
                      <span style="position: absolute; left: 0;">▸</span>
                      {{ item }}
                    </li>
                  </ul>
                </b-card-body>
              </b-card>
            </b-col>
          </b-row>
          <h2 class="mb-2 mt-2">
            Suitability
          </h2>
          <preference-analysis
            :preference-info="preferenceInfo"
            :suitability-info="suitabilityInfo"
            :selected-preference-id="selectedPreferenceId"
            :is-loading-preference="false"
            :is-loading-suitability="false"
            mode="single"
          />
        </div>
      </b-col>
    </b-row>

    <!-- Lifecycle -->
    <b-row id="lifecycle">
      <b-col cols="12">
        <h1 class="mb-2 mt-2">
          Lifecycle
        </h1>
        <b-card class="text-center">
          <p class="mb-0">
            This product has not yet closed for investment.
          </p>
          <p class="mb-1">
            Live product outcomes will be available after the strike date.
          </p>
        </b-card>
      </b-col>
    </b-row>

    <!-- Provider (matches ProductProvider.vue) -->
    <b-row id="provider">
      <b-col cols="12">
        <h1 class="mb-2 mt-2">
          Provider Information
        </h1>
        <div class="mb-0">
          <b-row class="match-height mb-0">
            <b-col
              cols="12"
              md="4"
            >
              <b-card title="Provider contact details:">
                <b-card-text>
                  <div
                    v-for="field in d.sidebar.providerContact.Show"
                    :key="field"
                  >
                    <strong>{{ field }}: </strong>
                    <span v-if="isLink.includes(field)">
                      <a
                        :href="d.sidebar.providerContact[field]"
                        target="_blank"
                      >{{ d.sidebar.providerContact.providerName }} {{ field.toLowerCase() }}</a>
                    </span>
                    <span v-else>{{ d.sidebar.providerContact[field] }}</span>
                  </div>
                </b-card-text>
              </b-card>
              <b-card title="Other current products from this provider">
                <b-card-text>
                  <div>
                    <strong>Next: </strong>
                    <span class="text-muted">{{ d.reportDisplay.next }}</span>
                    <br><br>
                    <span class="text-muted">{{ d.reportDisplay.count }} products from {{ d.reportDisplay.provider }}</span>
                  </div>
                </b-card-text>
              </b-card>
            </b-col>
            <b-col
              cols="12"
              md="8"
            >
              <b-card
                title="Investment Enquiry"
                class="h-100"
              >
                <b-card-text>
                  Contact the provider directly about this product.
                </b-card-text>
                <b-row>
                  <b-col cols="6">
                    <b-form-group label="Provider">
                      <b-form-input
                        :value="d.sidebar.providerContact.providerName"
                        readonly
                      />
                    </b-form-group>
                  </b-col>
                  <b-col cols="6">
                    <b-form-group label="Product Name">
                      <b-form-input
                        :value="d.reportTitle"
                        readonly
                      />
                    </b-form-group>
                  </b-col>
                </b-row>
                <b-row>
                  <b-col cols="6">
                    <b-form-group label="Name">
                      <b-form-input
                        value="John Smith"
                        readonly
                      />
                    </b-form-group>
                  </b-col>
                  <b-col cols="6">
                    <b-form-group label="Company">
                      <b-form-input
                        value="Acme Wealth Management"
                        readonly
                      />
                    </b-form-group>
                  </b-col>
                </b-row>
                <b-row>
                  <b-col cols="6">
                    <b-form-group label="Email Address">
                      <b-form-input
                        value="j.smith@acmewm.co.uk"
                        type="email"
                        readonly
                      />
                    </b-form-group>
                  </b-col>
                </b-row>
                <b-form-group label="Message">
                  <b-form-textarea
                    placeholder="Enter your message here..."
                    rows="3"
                    disabled
                  />
                </b-form-group>
                <div class="text-right">
                  <b-button
                    variant="primary"
                    disabled
                  >
                    Submit
                  </b-button>
                </div>
              </b-card>
            </b-col>
          </b-row>
        </div>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import {
  BRow,
  BCol,
  BCard,
  BCardText,
  BCardTitle,
  BCardBody,
  BAvatar,
  BButton,
  BImg,
  BTableSimple,
  BFormGroup,
  BFormInput,
  BFormTextarea,
} from 'bootstrap-vue'
import REPORT_DATA from '../../../test/getreportstructureoutput.json'
import SUITABILITY_DATA from '../../../test/reportsuitability.json'
import PreferenceAnalysis from '../products/breakdown/PreferenceAnalysis.vue'
import ComparisonChart from '../products/breakdown/charts/ComparisonChart.vue'
import PayoffChart from '../products/breakdown/charts/PayoffChart.vue'
import OutcomesCharts from '../products/breakdown/charts/OutcomesCharts.vue'

export default {
  name: 'ProductReportStatic',
  components: {
    BRow,
    BCol,
    BCard,
    BCardText,
    BCardTitle,
    BCardBody,
    BAvatar,
    BButton,
    BImg,
    BTableSimple,
    BFormGroup,
    BFormInput,
    BFormTextarea,
    ComparisonChart,
    PayoffChart,
    OutcomesCharts,
    PreferenceAnalysis,
  },
  data() {
    return {
      d: REPORT_DATA.content,
      baseURL: process.env.VUE_APP_BACKEND_PATH,
      suitabilityInfo: SUITABILITY_DATA,
      preferenceInfo: [{ preferenceid: 1, name: 'Default Preference' }],
      selectedPreferenceId: 1,
      isLink: ['Platform', 'Website'],
      targetMarket: [
        {
          sectionname: 'Investment Objectives',
          icon: 'TargetIcon',
          iconColor: 'light-success',
          data: [
            'Capital growth over the medium to long term',
            'Willing to accept some risk to achieve above-inflation returns',
            'Seeking defined outcome at maturity',
            'Not seeking regular income during the term',
            'Comfortable with returns linked to underlying fund performance',
          ],
        },
        {
          sectionname: 'Personal Circumstances',
          icon: 'UserIcon',
          iconColor: 'light-info',
          data: [
            'Advised retail investors',
            'Investment horizon of at least 10 years',
            'Does not require access to capital during the term',
            'Has other liquid assets to cover short-term needs',
          ],
        },
        {
          sectionname: 'Knowledge & Experience',
          icon: 'BookIcon',
          iconColor: 'light-info',
          data: [
            'Basic knowledge of structured deposits',
            'Understanding of conditional coupon mechanics',
            'Familiarity with autocall or kick-out features',
            'Experience with fixed-term savings or investment products',
            'Has received regulated financial advice',
            'Understands the role of the deposit taker',
          ],
        },
        {
          sectionname: 'Risk Tolerance',
          icon: 'AlertTriangleIcon',
          iconColor: 'light-danger',
          data: [
            'Low to medium risk appetite',
            'Comfortable with credit risk of deposit taker',
            'Understands that coupons are not guaranteed',
            'Accepts the possibility of receiving no income in some quarters',
          ],
        },
        {
          sectionname: 'Investment Process',
          icon: 'TrendingUpIcon',
          iconColor: 'light-primary',
          data: [
            'Advised sale only — not available execution-only',
            'Suitable for ISA, SIPP and direct investment wrappers',
            'Minimum investment £3,000 for direct investment',
            'Overall ISA limit applies for tax year 2025/2026',
            'Available for joint accounts, trusts and corporate investors',
          ],
        },
        {
          sectionname: 'Market View',
          icon: 'EyeIcon',
          iconColor: 'light-info',
          data: [
            'Neutral to mildly bullish view on underlying fund',
            'Expects coupon barrier of 80% to be met in most quarters',
            'Comfortable with autocall mechanism from year 3',
            'Not expecting significant sustained drawdown in underlying',
          ],
        },
        {
          sectionname: 'Counterparty Risk',
          icon: 'TruckIcon',
          iconColor: 'light-danger',
          data: [
            'Accepts credit risk of SG Issuer as deposit taker',
            'Aware that FSCS protection may apply up to £85,000',
            'Understands that issuer default could result in capital loss',
            'Has reviewed issuer credit rating: S&P A, Moody\'s A1, Fitch A-',
            'Comfortable with long-term fixed deposit structure',
          ],
        },
        {
          sectionname: 'Liquidity & Withdrawal',
          icon: 'ActivityIcon',
          iconColor: 'light-warning',
          data: [
            'No early access to capital during the 10-year term',
            'Early withdrawal not available except in exceptional circumstances',
            'Investor must be comfortable with full illiquidity for the term',
          ],
        },
        {
          sectionname: 'Tax Considerations',
          icon: 'PercentIcon',
          iconColor: 'light-warning',
          data: [
            'Coupons may be subject to income tax if held outside a wrapper',
            'ISA and SIPP wrappers provide tax-efficient treatment',
            'Investor should seek independent tax advice if uncertain',
            'No CGT liability expected on deposit-based structure',
            'Tax treatment depends on individual circumstances',
            'Product is PRIIPs SRI rated 3 out of 7',
          ],
        },
      ],
    }
  },
}
</script>

<style scoped>
.pdf-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 1.5rem;
  padding: 0.5rem 0;
}

.pdf-button {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  padding: 1.5rem 1.5rem;
  min-height: 180px;
  white-space: normal;
}

.pdf-icon {
  margin-top: 1.5rem;
  margin-bottom: 1rem;
  flex-shrink: 0;
}

.pdf-name {
  text-align: center;
  font-weight: 500;
  font-size: 0.95rem;
  line-height: 1.4;
  word-break: break-word;
}
</style>
